import { Component, ChangeDetectionStrategy, ViewEncapsulation, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { HmiKartOkuyucuComponent, KartOkumaOlayi, NfcDurum } from '@uygulamalar/hmi-pano/kart-okuyucu/kart-okuyucu.component';
import { HizliErisimComponent } from '@uygulamalar/hmi-pano/hizli-erisim/hizli-erisim.component';
import { BounceScrollDirective } from '@uygulamalar/hmi-pano/directives/bounce-scroll.directive';
import { KartWebSocketService } from '@uygulamalar/hmi-pano/kart-okuyucu/services/kart-websocket.service';
import { PdksDurumService } from '@uygulamalar/hmi-pano/services/pdks-durum.service';
import { PdksOfflineService } from '@uygulamalar/hmi-pano/services/pdks-offline.service';
import { PanoDurumService } from '@uygulamalar/hmi-pano/services/pano-durum.service';
import { AdminService } from '@uygulamalar/admin/services/admin.service';
import { FirmaService } from '@uygulamalar/firma-yonetim/services/firma.service';
import { AvatarService } from '@/src/app/servisler/avatar.service';
import { FikoYTKokBilesenComponent } from '@service/kok-bilesen/kok-bilesen.component';
import { Subject, takeUntil } from 'rxjs';
import { hmiAyarGetir } from '@uygulamalar/hmi-pano/config/hmi-parametre-setleri';

interface DonanımDurumu {
  nfc: boolean;
  bluetooth: boolean;
  seri: boolean;
  kamera: boolean;
  konum: boolean;
  titresim: boolean;
  yonSensoru: boolean;
  isikSensoru: boolean;
  ekranKilidi: boolean;
  batarya: { seviye: number; sarj: boolean; destekleniyor: boolean };
  ag: { cevrimici: boolean; tur: string; hiz: string };
}

@Component({
  selector: 'hmi-pano-pano',
  standalone: true,
  imports: [CommonModule, RouterModule, HmiKartOkuyucuComponent, HizliErisimComponent, BounceScrollDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  templateUrl: './pano.component.html',
  styleUrl: './pano.component.less',
})
export class HmiPanoPanoComponent extends FikoYTKokBilesenComponent implements OnInit, OnDestroy {
  get karanlikMod(): boolean { return document.body.classList.contains('hmi-karanlik'); }

  panoDurum = inject(PanoDurumService);

  // Kart durumu
  nfcDurum: 'bekliyor' | 'tanindi' | 'bilinmiyor' | 'giris-anim' | 'cikis-anim' = 'bekliyor';
  private _sonKartUid = '';
  _sonPersonel: any = null;
  private _baslangicZamani = Date.now();

  // Reklam slider
  reklamlar: any[] = [
    { ikon: '🚀', baslik: 'FikoYT Sistem v2.0', aciklama: 'Yapay zeka destekli üretim takibi, gerçek zamanlı analiz ve akıllı raporlama artık aktif!', badge: 'YENİ', cta: 'Keşfet →', bg: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #0ea5e9 100%)' },
    { ikon: '🏆', baslik: 'Ayın Yıldızları', aciklama: 'En yüksek verimlilik skoru ile Mart ayının en iyileri belirleniyor. Sıralaman kaçıncı?', badge: 'ÖDÜL', cta: null, bg: 'linear-gradient(135deg, #312e81 0%, #7c3aed 50%, #c084fc 100%)' },
    { ikon: '⚡', baslik: 'Hedef: 10.000 Adet', aciklama: 'Bu ayki üretim hedefimize %74 ulaştık. Son sprint başladı — birlikte başaracağız!', badge: 'HEDEF', cta: null, bg: 'linear-gradient(135deg, #064e3b 0%, #059669 50%, #34d399 100%)' },
    { ikon: '🛡️', baslik: 'İş Güvenliği Haftası', aciklama: 'Kask, gözlük ve eldiven kullanımı zorunludur. 365 gün kazasız çalışma hedefindeyiz!', badge: 'ÖNEMLİ', cta: null, bg: 'linear-gradient(135deg, #7c2d12 0%, #ea580c 50%, #fb923c 100%)' },
    { ikon: '🎁', baslik: 'Çalışan Memnuniyeti', aciklama: 'Yemekhane menüsü yenilendi! Önerilerinizi HMI panelden iletebilirsiniz.', badge: 'DUYURU', cta: 'Görüş Bildir →', bg: 'linear-gradient(135deg, #831843 0%, #db2777 50%, #f472b6 100%)' },
  ];
  reklamIndex = 0;
  private _reklamTimer: any;

  kisiselTikla(aksiyon: string) {
    console.log('[HMI] kisisel tikla:', aksiyon);
  }

  // Donanim durumu
  donanim: DonanımDurumu = {
    nfc: false, bluetooth: false, seri: false, kamera: false,
    konum: false, titresim: false, yonSensoru: false, isikSensoru: false,
    ekranKilidi: false,
    batarya: { seviye: 100, sarj: true, destekleniyor: false },
    ag: { cevrimici: navigator.onLine, tur: 'bilinmiyor', hiz: '' },
  };

  private _wakeLock: any = null;
  private _nfcController: AbortController | null = null;
  private _lightSensor: any = null;
  private _destroySubs: (() => void)[] = [];
  private _kartSorguDevam = false;
  private _erkenCekildi = false;
  private _hizliTapTimer: any = null;
  private _bilinmiyorTimer: any = null;
  private _destroy$ = new Subject<void>();

  constructor(
    private wsService: KartWebSocketService,
    private adminService: AdminService,
    private firmaService: FirmaService,
    private avatar: AvatarService,
    public pdksDurum: PdksDurumService,
    private pdksOffline: PdksOfflineService,
  ) { super(); }

  // ════════════════════════════════════════════════
  // LIFECYCLE
  // ════════════════════════════════════════════════

  ngOnInit() {
    this.initHardwareAPIs();

    const reklamAraligi = hmiAyarGetir<number>('reklamAraligi', 5) * 1000;
    this._reklamTimer = setInterval(() => {
      this.reklamIndex = (this.reklamIndex + 1) % this.reklamlar.length;
      this.CD.detectChanges();
    }, reklamAraligi);

    if (hmiAyarGetir<boolean>('olayLoglama', true)) {
      this.wsService.olayGonder({
        olay_kodu: 'sayfa_acildi',
        aciklama: 'HMI Pano sayfası açıldı',
        veri: { sayfa: 'pano', url: window.location.href },
      });
    }

    this.wsService.veriGeldi$.pipe(takeUntil(this._destroy$)).subscribe(veri => {
      if (/(cihaz.?koptu|baglanti.{0,3}kesildi)/i.test(veri)) {
        this.nfcSimule('bekliyor');
      }
    });

  }

  ngOnDestroy() {
    super.ngOnDestroy();
    if (hmiAyarGetir<boolean>('olayLoglama', true)) {
      this.wsService.olayGonder({
        olay_kodu: 'sayfa_kapandi',
        aciklama: 'HMI Pano sayfası kapandı',
        veri: { sayfa: 'pano' },
      });
    }
    this._destroy$.next();
    this._destroy$.complete();
    if (this._bilinmiyorTimer) clearTimeout(this._bilinmiyorTimer);
    if (this._hizliTapTimer) clearTimeout(this._hizliTapTimer);
    if (this._reklamTimer) clearInterval(this._reklamTimer);
    this._destroySubs.forEach(fn => fn());
    this.releaseWakeLock();
    if (this._nfcController) this._nfcController.abort();
    if (this._lightSensor) { try { this._lightSensor.stop(); } catch (_) {} }
  }

  // ════════════════════════════════════════════════
  // NAVIGASYON — route-based state yönetimi
  // ════════════════════════════════════════════════

  private _navigate(childPath: string) {
    this.router.navigate([childPath || '.'], { relativeTo: this.route });
  }

  // ════════════════════════════════════════════════
  // KART OKUYUCU OLAYLARI
  // ════════════════════════════════════════════════

  async onKartOkundu(olay: KartOkumaOlayi) {
    if (!olay.uid || olay.uid.trim().length < 2) return;

    if (this._hizliTapTimer) { clearTimeout(this._hizliTapTimer); this._hizliTapTimer = null; }
    this._erkenCekildi = false;
    this._kartSorguDevam = true;

    // Anlık UI tepkisi → yukleniyor route'una git
    this._navigate('yukleniyor');
    this.CD.detectChanges();

    const olayLoglamaAktif = hmiAyarGetir<boolean>('olayLoglama', true);

    try {
      const firmaId = hmiAyarGetir<string>('firmaId', '');
      const p = await this.firmaService.kartSorgula(firmaId, olay.uid);
      this._kartSorguDevam = false;

      if (this._erkenCekildi) {
        this._erkenCekildi = false;
        await this._kartTanidiktan(p, olay, firmaId, olayLoglamaAktif);
        this.onKartCekildi(olay);
        return;
      }
      await this._kartTanidiktan(p, olay, firmaId, olayLoglamaAktif);
    } catch (err) {
      this._kartSorguDevam = false;
      this._navigate('.');
      this.CD.detectChanges();
      const baslangicMs = Date.now() - this._baslangicZamani;
      if (baslangicMs < 5000) return;
      if (olayLoglamaAktif) {
        this.wsService.olayGonder({
          olay_kodu: 'kart_okuma_hatasi',
          aciklama: `Kart okuma hatası: ${olay.uid}`,
          veri: { kartUid: olay.uid, hata: String(err) },
        });
      }
      this.nfcSimule('bilinmiyor');
    }
  }

  private async _kartTanidiktan(p: any, olay: KartOkumaOlayi, firmaId: string, olayLoglamaAktif: boolean) {
    const pAd = p?.ad || '';
    const pSoyad = p?.soyad || '';
    const pIsimTam = `${pAd} ${pSoyad}`.trim();

    if (olayLoglamaAktif) {
      this.adminService.iotOlayKaydet({
        tip: 'kart_koyuldu', kartUid: olay.uid,
        personelId: p?._id, personelIsim: p ? pIsimTam : undefined,
      }).catch(() => {});
      this.wsService.olayGonder({
        olay_kodu: 'kart_koyuldu',
        aciklama: p ? `Kart okutuldu: ${pIsimTam}` : `Bilinmeyen kart okutuldu: ${olay.uid}`,
        veri: { kartUid: olay.uid, personelId: p?._id, firmaId, kaynak: (olay as any).kaynak || 'seri_port' },
      });
    }

    if (p) {
      const now = new Date();
      const saat = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;

      if (olayLoglamaAktif) {
        this.wsService.olayGonder({
          olay_kodu: 'personel_tanindi',
          aciklama: `${pIsimTam} kartını okuttu`,
          veri: { kartUid: olay.uid, personelId: p._id, personelIsim: pIsimTam, unvan: p.unvan || '', girisSaati: saat, departman: p.departman?.departmanAdi || '' },
        });
      }

      this._sonKartUid = olay.uid;
      this._sonPersonel = p;

      // PdksDurumService
      this.pdksDurum.aktifPersonel.set(p);
      this.pdksDurum.firmaId.set(firmaId);
      this.pdksDurum.cihazId.set(localStorage.getItem('fikoyt_cihaz_kimlik') || '');
      await this.pdksDurum.pdksAyarlarYukle(firmaId);
      await this.pdksDurum.gunlukVeriYukle(new Date());
      this.pdksDurum.timerBaslat();

      const isim = pAd || p._uyeIsim || '';
      const soyIsim = pSoyad || p._uyeSoyisim || '';
      const profilResmiId = String(p._uyeResimId || '');
      const fapiUrl = this.FikoYT.fapiUrl || '';
      const resimUrl = profilResmiId && fapiUrl ? `${fapiUrl}/fapiv1/fikoyt.com.FikoYT.sistem.dosyalar.${profilResmiId}.resim?boyut=256x?&thumbnail=true` : '';

      // PanoDurumService güncelle
      this.panoDurum.taninanKisi.set({ isim, soyIsim, rol: p.unvan || p.gorev || '', resimUrl, girisSaati: saat });
      this.panoDurum.sonPersonel.set(p);
      this.panoDurum.kisiYetkileri.set(p._uyeYetkiler || []);
      this.panoDurum.sonGirisler.update(onceki => [
        { isim, soyisim: soyIsim, rol: p.unvan || p.gorev || '', saat, resimUrl },
        ...onceki.filter(g => !(g.isim === isim && g.soyisim === soyIsim)),
      ].slice(0, 6));

      this.nfcDurum = 'tanindi';
      this.titret([80]);
      this._navigate('personel');
      this.CD.detectChanges();
    } else {
      const baslangicMs = Date.now() - this._baslangicZamani;
      if (baslangicMs < 5000) {
        this._navigate('.');
        this.CD.detectChanges();
        return;
      }
      this._sonKartUid = olay.uid;
      this._sonPersonel = null;
      if (olayLoglamaAktif) {
        this.wsService.olayGonder({
          olay_kodu: 'bilinmeyen_kart',
          aciklama: `Tanınmayan kart okutuldu: ${olay.uid}`,
          veri: { kartUid: olay.uid },
        });
      }
      this.nfcSimule('bilinmiyor');
    }
  }

  async onKartCekildi(olay: KartOkumaOlayi) {
    if (this._hizliTapTimer) { clearTimeout(this._hizliTapTimer); this._hizliTapTimer = null; }

    if (this._kartSorguDevam) {
      this._erkenCekildi = true;
      return;
    }

    const olayLoglamaAktif = hmiAyarGetir<boolean>('olayLoglama', true);
    const sp = this._sonPersonel;
    const personelIsim = sp ? `${sp.ad || ''} ${sp.soyad || ''}`.trim() : undefined;

    if (olayLoglamaAktif) {
      this.adminService.iotOlayKaydet({
        tip: 'kart_cekildi', kartUid: olay.uid,
        personelId: this._sonPersonel?._id, personelIsim,
      }).catch(() => {});
      this.wsService.olayGonder({
        olay_kodu: 'kart_cekildi',
        aciklama: personelIsim ? `${personelIsim} kartını çekti` : `Kart çekildi: ${olay.uid}`,
        veri: { kartUid: olay.uid, personelId: this._sonPersonel?._id, personelIsim },
      });
    }

    this._sonKartUid = '';
    this._sonPersonel = null;
    this.panoDurum.sifirla();
    this.pdksDurum.sifirla();
    this.nfcSimule('bekliyor');
  }

  onNfcDurumDegisti(durum: NfcDurum) {
    if (durum === 'giris-anim' || durum === 'cikis-anim') return;
    if (hmiAyarGetir<boolean>('olayLoglama', true)) {
      this.wsService.olayGonder({
        olay_kodu: 'durum_degisti',
        aciklama: `NFC durum değişti: ${durum}`,
        veri: { oncekiDurum: this.nfcDurum, yeniDurum: durum },
      });
    }
    this.nfcSimule(durum as any);
  }

  // ════════════════════════════════════════════════
  // NFC SIMULATION
  // ════════════════════════════════════════════════

  nfcSimule(durum: 'bekliyor' | 'tanindi' | 'bilinmiyor') {
    if (this._bilinmiyorTimer) { clearTimeout(this._bilinmiyorTimer); this._bilinmiyorTimer = null; }
    this.nfcDurum = durum;

    if (durum === 'tanindi') {
      this.titret([80]);
      this._navigate('personel');
    } else if (durum === 'bilinmiyor') {
      this.titret([200, 100, 200]);
      this._navigate('yetkisiz');
    } else {
      this._navigate('.');
    }
    this.CD.detectChanges();
  }

  // ════════════════════════════════════════════════
  // HELPERS
  // ════════════════════════════════════════════════

  get aktifDonanımSayisi(): number {
    let c = 0;
    if (this.donanim.nfc) c++;
    if (this.donanim.bluetooth) c++;
    if (this.donanim.seri) c++;
    if (this.donanim.kamera) c++;
    if (this.donanim.konum) c++;
    if (this.donanim.titresim) c++;
    if (this.donanim.isikSensoru) c++;
    if (this.donanim.ekranKilidi) c++;
    if (this.donanim.batarya.destekleniyor) c++;
    return c;
  }

  sifirla() {
    this._sonKartUid = '';
    this._sonPersonel = null;
    this.panoDurum.sifirla();
    this.pdksDurum.sifirla();
    this.nfcSimule('bekliyor');
  }

  // ════════════════════════════════════════════════
  // HTML5 HARDWARE APIS
  // ════════════════════════════════════════════════

  private async initHardwareAPIs() {
    this.detectCapabilities();
    await Promise.allSettled([
      this.initNFC(), this.initBattery(), this.initWakeLock(),
      this.initAmbientLight(), this.initScreenOrientation(),
    ]);
    this.initNetwork();
    this.CD.markForCheck();
  }

  private async initNFC() {
    if (!('NDEFReader' in window)) return;
    this.donanim.nfc = true;
    try {
      const ndef = new (window as any).NDEFReader();
      this._nfcController = new AbortController();
      await ndef.scan({ signal: this._nfcController.signal });
      ndef.addEventListener('reading', (event: any) => {
        this.nfcSimule('tanindi');
        this.titret([100]);
        this.CD.markForCheck();
      });
      ndef.addEventListener('readingerror', () => {
        this.nfcSimule('bilinmiyor');
        this.titret([200, 100, 200]);
        if (hmiAyarGetir<boolean>('olayLoglama', true)) {
          this.wsService.olayGonder({ olay_kodu: 'nfc_okuma_hatasi', aciklama: 'NFC kart okuma hatası oluştu', veri: { kaynak: 'web_nfc' } });
        }
      });
    } catch (_) {}
  }

  private async initBattery() {
    if (!('getBattery' in navigator)) return;
    try {
      const battery = await (navigator as any).getBattery();
      this.donanim.batarya = { seviye: Math.round(battery.level * 100), sarj: battery.charging, destekleniyor: true };
      const update = () => { this.donanim.batarya.seviye = Math.round(battery.level * 100); this.donanim.batarya.sarj = battery.charging; this.CD.markForCheck(); };
      battery.addEventListener('levelchange', update);
      battery.addEventListener('chargingchange', update);
      this._destroySubs.push(() => { battery.removeEventListener('levelchange', update); battery.removeEventListener('chargingchange', update); });
    } catch (_) {}
  }

  private initNetwork() {
    const conn: any = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;
    if (conn) {
      this.donanim.ag.tur = conn.effectiveType || conn.type || 'wifi';
      this.donanim.ag.hiz = conn.downlink ? `${conn.downlink} Mbps` : '';
      const update = () => { this.donanim.ag.tur = conn.effectiveType || conn.type || 'wifi'; this.donanim.ag.hiz = conn.downlink ? `${conn.downlink} Mbps` : ''; this.CD.markForCheck(); };
      conn.addEventListener('change', update);
      this._destroySubs.push(() => conn.removeEventListener('change', update));
    }
    this.donanim.ag.cevrimici = navigator.onLine;
    const onlineH = () => { this.donanim.ag.cevrimici = true; this.CD.markForCheck(); if (hmiAyarGetir<boolean>('olayLoglama', true)) this.wsService.olayGonder({ olay_kodu: 'ag_durumu', aciklama: 'İnternet bağlantısı yeniden kuruldu', veri: { durum: 'cevrimici' } }); };
    const offlineH = () => { this.donanim.ag.cevrimici = false; this.CD.markForCheck(); if (hmiAyarGetir<boolean>('olayLoglama', true)) this.wsService.olayGonder({ olay_kodu: 'ag_durumu', aciklama: 'İnternet bağlantısı kesildi', veri: { durum: 'cevrimdisi' } }); };
    window.addEventListener('online', onlineH);
    window.addEventListener('offline', offlineH);
    this._destroySubs.push(() => { window.removeEventListener('online', onlineH); window.removeEventListener('offline', offlineH); });
  }

  private async initWakeLock() {
    if (!('wakeLock' in navigator)) return;
    this.donanim.ekranKilidi = true;
    if (!hmiAyarGetir<boolean>('ekranAcikTut', true)) return;
    try {
      this._wakeLock = await (navigator as any).wakeLock.request('screen');
      this._wakeLock.addEventListener('release', () => { this._wakeLock = null; });
    } catch (_) {}
    const handler = async () => {
      if (document.visibilityState === 'visible' && !this._wakeLock) {
        try { this._wakeLock = await (navigator as any).wakeLock.request('screen'); } catch (_) {}
      }
    };
    document.addEventListener('visibilitychange', handler);
    this._destroySubs.push(() => document.removeEventListener('visibilitychange', handler));
  }

  private async releaseWakeLock() {
    if (this._wakeLock) { try { await this._wakeLock.release(); } catch (_) {} this._wakeLock = null; }
  }

  private initAmbientLight() {
    if (!('AmbientLightSensor' in window)) return;
    this.donanim.isikSensoru = true;
    try {
      this._lightSensor = new (window as any).AmbientLightSensor();
      this._lightSensor.addEventListener('reading', () => {});
      this._lightSensor.start();
    } catch (_) {}
  }

  private async initScreenOrientation() {
    if (!screen.orientation) return;
    try { await (screen.orientation as any).lock('landscape'); } catch (_) {}
  }

  private detectCapabilities() {
    this.donanim.nfc = 'NDEFReader' in window;
    this.donanim.bluetooth = 'bluetooth' in navigator;
    this.donanim.seri = 'serial' in navigator;
    this.donanim.kamera = !!(navigator.mediaDevices?.getUserMedia);
    this.donanim.konum = 'geolocation' in navigator;
    this.donanim.titresim = 'vibrate' in navigator;
    this.donanim.yonSensoru = 'DeviceOrientationEvent' in window;
  }

  titret(desen: number[]) {
    if (!hmiAyarGetir<boolean>('titresimGeriBildirim', true)) return;
    if ('vibrate' in navigator) navigator.vibrate(desen);
  }

  // ════════════════════════════════════════════════
  // TEST: Fikri Topaloğlu simülasyonu
  // ════════════════════════════════════════════════

  async testKartKoyuldu() {
    this._navigate('yukleniyor');
    this.CD.detectChanges();

    try {
      const p = await this.firmaService.personelAraIsimle('Fikri', 'Topaloğlu');
      if (p) {
        const now = new Date();
        const saat = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
        const isim = p.ad || p._uyeIsim || 'Fikri';
        const soyIsim = p.soyad || p._uyeSoyisim || 'Topaloğlu';
        const profilResmiId = String(p._uyeResimId || '');
        const fapiUrl = this.FikoYT.fapiUrl || '';
        const resimUrl = profilResmiId && fapiUrl ? `${fapiUrl}/fapiv1/fikoyt.com.FikoYT.sistem.dosyalar.${profilResmiId}.resim?boyut=256x?&thumbnail=true` : '';

        this.panoDurum.taninanKisi.set({ isim, soyIsim, rol: p.unvan || p.gorev || '', resimUrl, girisSaati: saat });
        this._sonPersonel = p;
        this.panoDurum.sonPersonel.set(p);
        this.panoDurum.kisiYetkileri.set((p as any)._uyeYetkiler || []);
        const firmaId = hmiAyarGetir<string>('firmaId', '');
        this.pdksDurum.aktifPersonel.set(p);
        this.pdksDurum.firmaId.set(firmaId);
        this.pdksDurum.cihazId.set(localStorage.getItem('fikoyt_cihaz_kimlik') || '');
        await this.pdksDurum.pdksAyarlarYukle(firmaId);
        await this.pdksDurum.gunlukVeriYukle(new Date());
        this.pdksDurum.timerBaslat();
        this.panoDurum.sonGirisler.update(onceki => [
          { isim, soyisim: soyIsim, rol: p.unvan || p.gorev || '', saat, resimUrl },
          ...onceki.filter(g => !(g.isim === isim && g.soyisim === soyIsim)),
        ].slice(0, 6));

        this.nfcDurum = 'tanindi';
        this._navigate('personel');
        this.CD.detectChanges();
      } else {
        this.nfcSimule('bilinmiyor');
      }
    } catch (err) {
      this._navigate('.');
      this.CD.detectChanges();
    }
  }

  testKartCekildi() {
    this._sonPersonel = null;
    this.panoDurum.sifirla();
    this.pdksDurum.sifirla();
    this.nfcSimule('bekliyor');
  }
}
